import React from 'react';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {css} from 'aphrodite';

import Markdown from 'components/Markdown';
import BoxedIsi from 'components/BoxedIsi';

const REQUEST_REP_PATTERN = /request|rep|contact/i;
const isRequestRepLink = (name, href) =>
  REQUEST_REP_PATTERN.test(name || '') || REQUEST_REP_PATTERN.test(href || '');

const Footer = (props) => {
  const {settings, styleSheet, logoAssets, toggleExpandBoxedIsi, isBoxedIsiExpanded} = props;

  const linkRenderer = (linkProps) => {
    const linkText = typeof linkProps.children === 'string' ? linkProps.children : '';
    const trackAttrs = isRequestRepLink(linkText, linkProps.href)
      ? {'data-track': 'request-rep', 'data-track-label': linkText}
      : {};
    // Check if link is internal (doesn't start with http:// or https://)
    const isInternalLink = linkProps.href && !linkProps.href.match(/^https?:\/\//i);
    // Internal links open in same page, external links respect linkNewTab setting
    const target = isInternalLink ? null : (settings.footer.linkNewTab ? '_blank' : null);
    const rel = isInternalLink ? null : (settings.footer.linkNewTab ? 'noopener noreferrer' : null);
    return (
      <a
        target={target}
        rel={rel}
        href={linkProps.href}
        className={css(styleSheet.footerLinkInNonLinkSection)}
        {...trackAttrs}
      >
        {linkProps.children}
      </a>
    );
  };

  let footerGridElements = [];
  let footerElementIndex = 0;
  settings.footer.layout.forEach((rowArray, elementIndex) => {
    let footerRowElements = [];
    rowArray.forEach((column, columnIndex) => {
      if (column.element === 'logo') {
        footerRowElements.push(
          <Col key={`logo-${columnIndex}`} sm={column.gridWidth} className={`offset-sm-${column.offset} ${css(styleSheet[`footerElement${footerElementIndex}`])}`}>
            {settings.footer.logoLink ? (
              <a href={settings.footer.logoLink} target={settings.footer.logoLinkNewTab ? '_blank' : null} rel={settings.footer.logoLinkNewTab ? 'noopener noreferrer' : null}>
                <img src={logoAssets[settings.footer.logo.file]} className={`${css(styleSheet.footerLogo)} d-block img-fluid` } alt={settings.footer.logo.alt ?? 'logo'} />
              </a>) : (
              <img src={logoAssets[settings.footer.logo.file]} className={`${css(styleSheet.footerLogo)} d-block img-fluid` } alt={settings.footer.logo.alt ?? 'logo'} />)
            }
            {column.markdown &&
              <Markdown components={{a: linkRenderer}}>{column.markdown}</Markdown>
            }
          </Col>
        );
      } else if (column.element === 'links') {
        footerRowElements.push(
          <Col key={`links-${columnIndex}`} sm={column.gridWidth} className={`offset-sm-${column.offset} ${css(styleSheet[`footerElement${footerElementIndex}`])}`}>
            {settings.footer.links.map((link) => {
              const trackAttrs = isRequestRepLink(link.name, link.href)
                ? {'data-track': 'request-rep', 'data-track-label': link.name}
                : {};
              return (<a key={link.name} className={css(styleSheet.footerLink)} href={link.href} {...trackAttrs}>{link.name}</a>);
            })}
          </Col>
        );
      } else if (column.element === 'content') {
        footerRowElements.push(
          <Col key={`content-${columnIndex}`} sm={column.gridWidth} className={`offset-sm-${column.offset} ${css(styleSheet[`footerElement${footerElementIndex}`])}`}>
            <Markdown components={{a: linkRenderer}}>
              {settings.footer.content.markdown}
            </Markdown>
          </Col>
        );
      } else if (column.element === 'additionalContent') {
        footerRowElements.push(
          <Col key={`additional-content-${columnIndex}`} sm={column.gridWidth} className={`offset-sm-${column.offset} ${css(styleSheet[`footerElement${footerElementIndex}`])}`}>
            <Markdown components={{a: linkRenderer}}>{column.markdown}</Markdown>
            {column.html && <div dangerouslySetInnerHTML={{__html: column.html}} />}
          </Col>
        );
      }
      footerElementIndex++;
    });
    footerGridElements.push(
      <Row key={`footer-element-${elementIndex}`} className={css(styleSheet.footerRows)}>
        {footerRowElements}
      </Row>
    );
  });

  return (
    <footer data-component="Footer">
      <BoxedIsi
        enabled={!settings.isi.boxedIsi.disabled}
        settings={settings.isi}
        styleSheet={styleSheet}
        source={settings.isi.boxedIsi.content.markdown.length ? settings.isi.boxedIsi.content.markdown : settings.isi.content.markdown}
        topRightLink={settings.isi.boxedIsi.topRightLink}
        fixed={settings.isi.boxedIsi.fixed}
        expandable={settings.isi.boxedIsi.expandable}
        toggleExpandBox={toggleExpandBoxedIsi}
        isExpanded={isBoxedIsiExpanded}
      />
      <div className={css(styleSheet.footer)}>
        <Container>
          {footerGridElements}
        </Container>
      </div>
    </footer>
  );
};

Footer.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  logoAssets: PropTypes.object.isRequired,
  toggleExpandBoxedIsi: PropTypes.func.isRequired,
  isBoxedIsiExpanded: PropTypes.bool.isRequired,
};

export default Footer;
